import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import {
  useCreateSite,
  useSites,
  slugify,
  useAccountDatabases,
} from '@wordpuppi/shared';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, FilePlus2, Link2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { ThemeToggle } from '@/components/ThemeToggle';
import { markGetStartedActive } from '@/components/GetStartedChecklist';
import { AiSiteWizard } from '@/components/sites/AiSiteWizard';

export const Route = createFileRoute('/sites/new')({
  // `?ai=1` opens on the AI path (AB#642), `?port=1` on its "bring a site over"
  // variant (which implies AI). Numbers, not strings: TanStack JSON-encodes
  // string params (`?ai=%221%22`).
  validateSearch: (search: { ai?: unknown; port?: unknown }): { ai?: number; port?: number } => ({
    ai: search.ai || search.port ? 1 : undefined,
    port: search.port ? 1 : undefined,
  }),
  component: NewSitePage,
});

function NewSitePage() {
  const navigate = useNavigate();
  const { ai, port } = Route.useSearch();
  const [mode, setMode] = useState<'basics' | 'ai' | 'port' | null>(port ? 'port' : ai ? 'ai' : null);
  const [wizardBusy, setWizardBusy] = useState(false);
  // Auth check handled by parent route (sites.tsx)
  const createSite = useCreateSite();
  const submitting = useRef(false);
  const createdSlug = useRef<string | null>(null);
  const { data: sitesData } = useSites();
  const { data: databasesData, isLoading: databasesLoading } = useAccountDatabases();

  // First run = no sites yet. sites.index redirects the empty case here, so Cancel
  // is hidden (there's nowhere to go back to).
  const isFirstRun = (sitesData?.data?.length ?? 0) === 0;

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [domain, setDomain] = useState('');

  // 'internal-sqlite' = built-in storage (the default), number = saved connection.
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<
    number | 'internal-sqlite'
  >('internal-sqlite');

  // Auto-generate slug from name
  useEffect(() => {
    if (!slugManuallyEdited && name) {
      setSlug(slugify(name));
    }
  }, [name, slugManuallyEdited]);

  const databases = databasesData?.data ?? [];
  const selectedDatabase =
    typeof selectedDatabaseId === 'number'
      ? databases.find((d) => d.id === selectedDatabaseId)
      : null;

  const handleDropdownChange = (value: string) => {
    if (value === 'add-new') {
      navigate({ to: '/settings/databases' });
    } else if (value === 'internal-sqlite') {
      setSelectedDatabaseId('internal-sqlite');
    } else {
      setSelectedDatabaseId(parseInt(value, 10));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    try {
      if (!name.trim()) throw new Error('Enter a site name.');
      if (selectedDatabaseId !== 'internal-sqlite' && !selectedDatabase) throw new Error('Choose an available database.');
      if (createdSlug.current) {
        await navigate({ to: '/sites/$siteSlug/posts', params: { siteSlug: createdSlug.current } });
        return;
      }
      // Default path is internal SQLite: db_type 'sqlite' with no saved
      // connection. Advanced picks a saved connection by id instead. Theme is a
      // site setting that already defaults to 'default', so no extra call needed.
      const result = await createSite.mutateAsync({
        name: name.trim(),
        slug: slug || undefined,
        db_type:
          selectedDatabaseId === 'internal-sqlite'
            ? 'sqlite'
            : selectedDatabase?.db_type ?? 'sqlite',
        database_connection_id:
          typeof selectedDatabaseId === 'number' ? selectedDatabaseId : undefined,
        domain: domain || undefined,
      });
      if (!result.data?.slug) throw new Error('Site creation could not be confirmed. Check your Sites list.');
      const newSlug = result.data.slug;
      createdSlug.current = newSlug;
      markGetStartedActive(newSlug);
      toast.success('Site created');
      await navigate({ to: '/sites/$siteSlug/posts', params: { siteSlug: newSlug } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create site');
    } finally { submitting.current = false; }
  };

  return (
    <div className="max-w-xl mx-auto p-6">
      <div className="mb-2 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          {isFirstRun ? 'Create your first site' : 'New Site'}
        </h1>
        <ThemeToggle />
      </div>
      <p className="mb-6 text-sm text-muted-foreground">
        {mode === 'port' ? 'Bring your pages, copy and images into WordPuppi from a site you own.'
          : mode === 'ai' ? 'Give Baxter a starting point. You’ll review the plan together before it builds.'
          : mode === 'basics' ? 'Start with a blank site and make it yours at your own pace.'
          : 'How would you like to start?'}
      </p>
      {!mode ? <div className="divide-y rounded-xl border bg-card">
        {([
          ['basics', FilePlus2, 'Start blank', 'A clean site, ready for your first page.'],
          ['ai', Sparkles, 'Build with Baxter', 'Describe your idea and build it together.'],
          ['port', Link2, 'Bring a site over', 'Keep the copy and character of a site you own.'],
        ] as const).map(([value, Icon, title, description]) => <button key={value} type="button"
          className="flex w-full items-center gap-4 p-5 text-left hover:bg-muted/50 focus-visible:outline focus-visible:outline-ring"
          onClick={() => setMode(value)}>
          <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="flex-1"><span className="block font-medium">{title}</span><span className="mt-1 block text-sm text-muted-foreground">{description}</span></span>
          <ArrowRight className="size-4 shrink-0" aria-hidden />
        </button>)}
      </div> : <>
        <Button type="button" variant="ghost" size="sm" className="mb-5 -ml-2" disabled={wizardBusy || createSite.isPending} onClick={() => setMode(null)}>
          <ArrowLeft /> Change starting point
        </Button>
        {mode === 'ai' || mode === 'port' ? (
          <AiSiteWizard key={mode} port={mode === 'port'} onBusyChange={setWizardBusy} />
        ) : (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Site Name</Label>
          <Input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My Awesome Blog"
            required
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugManuallyEdited(true);
            }}
            placeholder="my-awesome-blog"
            required
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
          />
          <p className="text-sm text-muted-foreground">
            URL-friendly identifier for this site
          </p>
        </div>

        <details className="rounded-lg border border-border bg-card px-4 py-3">
          <summary className="cursor-pointer select-none text-sm font-medium text-muted-foreground hover:text-foreground">
            Advanced options
          </summary>
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label>Database</Label>
              <Select
                value={selectedDatabaseId.toString()}
                onValueChange={handleDropdownChange}
                disabled={databasesLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a database..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal-sqlite">Internal Built-in</SelectItem>
                  {databases.map((db) => (
                    <SelectItem key={db.id} value={db.id.toString()}>
                      {db.name} ({db.db_type})
                    </SelectItem>
                  ))}
                  <SelectItem value="add-new">+ Add a database...</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Leave as Internal Built-in for a local SQLite database. You can
                migrate to an external database later.
              </p>
            </div>

            {selectedDatabase && (
              <Card className="bg-muted">
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground mb-1">Connection:</p>
                  <p className="font-mono text-sm break-all">
                    {selectedDatabase.connection_preview}
                  </p>
                  {selectedDatabase.ssh_enabled && (
                    <p className="text-sm text-muted-foreground mt-2">
                      SSH: {selectedDatabase.ssh_username}@{selectedDatabase.ssh_host}
                      {selectedDatabase.ssh_port !== 22 && `:${selectedDatabase.ssh_port}`}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              <Label htmlFor="domain">Domain (optional)</Label>
              <Input
                id="domain"
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="blog.example.com"
              />
            </div>
          </div>
        </details>

        <div className="flex gap-2 pt-4">
          <Button type="submit" disabled={createSite.isPending}>
            {createSite.isPending ? 'Creating...' : 'Create Site'}
          </Button>
          {!isFirstRun && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate({ to: '/sites' })}
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
      )}
      </>}
    </div>
  );
}
